import React, { useContext } from 'react';
import { AuthContext } from '../App';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Bell, BellOff, AlertCircle, CheckCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function NotificationSettings() {
  const { user } = useContext(AuthContext);
  const { isSubscribed, isLoading, error, permission, subscribe, unsubscribe } = usePushNotifications(user);

  const handleToggleNotifications = async () => {
    if (isSubscribed) {
      const result = await unsubscribe();
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } else {
      const result = await subscribe();
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <p className="text-gray-600">Giriş yapmanız gerekiyor</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-4xl mx-auto pt-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <Send className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            Bildirim Ayarları
          </h1>
          <p className="text-gray-600">
            Push bildirimleri yönetin ve gerçek zamanlı güncellemeler alın
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {/* Browser Support Error */}
          {error && error.includes('desteklemiyor') && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Tarayıcı Uyarısı</p>
                <p className="text-sm text-yellow-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Permission Denied */}
          {permission === 'denied' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Bildirim İzni Engellendi</p>
                <p className="text-sm text-red-700 mt-1">
                  Tarayıcınızın ayarlarından OnxFlow için bildirimlere izin vermeniz gerekiyor.
                </p>
              </div>
            </div>
          )}

          {/* Status Card */}
          <div className="mb-6">
            <div className={`p-6 rounded-xl border-2 ${
              isSubscribed 
                ? 'bg-green-50 border-green-200' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {isSubscribed ? (
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Bell className="w-6 h-6 text-green-600" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <BellOff className="w-6 h-6 text-gray-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Push Bildirimler
                    </h3>
                    <p className={`text-sm mt-1 ${
                      isSubscribed ? 'text-green-700' : 'text-gray-600'
                    }`}>
                      {isSubscribed ? 'Aktif - Bildirimler alıyorsunuz' : 'Devre Dışı'}
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={handleToggleNotifications}
                  disabled={isLoading || permission === 'denied' || (error && error.includes('desteklemiyor'))}
                  variant={isSubscribed ? 'destructive' : 'default'}
                  size="lg"
                  className={`min-w-[140px] ${
                    !isSubscribed 
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg' 
                      : ''
                  }`}
                >
                  {isLoading ? (
                    'İşlem yapılıyor...'
                  ) : isSubscribed ? (
                    <>
                      <BellOff className="w-4 h-4 mr-2" />
                      Devre Dışı Bırak
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Aktif Et
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Info Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Bildirimler Hakkında
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Gerçek Zamanlı Güncellemeler</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Size atanan yeni görevler, yapılan yorumlar ve önemli güncellemeler hakkında anında bildirim alın
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Çalışma Saatleri Dışında</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Tarayıcı kapalıyken bile önemli bildirimler cihazınıza ulaşır
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Gizlilik ve Güvenlik</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Bildirimleriniz şifreli olarak gönderilir ve sadece sizin cihazınızda görüntülenir
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <details className="text-sm text-gray-600">
              <summary className="cursor-pointer font-medium text-gray-900 mb-2">
                Teknik Detaylar
              </summary>
              <div className="space-y-2 mt-2 pl-4">
                <p><span className="font-medium">Durum:</span> {isSubscribed ? 'Aktif' : 'Pasif'}</p>
                <p><span className="font-medium">İzin:</span> {permission === 'granted' ? 'Verildi' : permission === 'denied' ? 'Reddedildi' : 'Bekleniyor'}</p>
                <p><span className="font-medium">Tarayıcı Desteği:</span> {
                  'serviceWorker' in navigator && 'PushManager' in window ? 'Destekleniyor' : 'Desteklenmiyor'
                }</p>
              </div>
            </details>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">Yardım</p>
              <p className="text-sm text-blue-800 mt-1">
                Bildirimleri aktif etmekte sorun yaşıyorsanız, tarayıcınızın ayarlarından OnxFlow için bildirimlere izin verdiğinizden emin olun.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
